import { m as motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FunctionIllustration } from "@/components/member-showcase-illustration";
import { defaultViewport } from "@/lib/motion";
import {
  getFunctionPath,
  MEMBER_SHOWCASE_FUNCTIONS,
  type MemberShowcaseFunctionSlug,
} from "@/lib/member-showcase";

const accentColors = [
  "border-t-primary",
  "border-t-primary/80",
  "border-t-secondary",
  "border-t-secondary/80",
  "border-t-accent",
  "border-t-accent/80",
];

const pullQuotes = [
  {
    quote: "Helped me move from skeptic to champion, from uncertain to having frameworks for a path forward.",
    name: "Matthew Rond",
    company: "IO Psychology",
  },
  {
    quote: "This course was a game-changer. GenAI isn't a magic wand, it's a thought partner.",
    name: "Alexia Sophos",
    company: "AgAmerica",
  },
];

const FunctionTile = ({
  slug,
  title,
  framing,
  index,
}: {
  slug: MemberShowcaseFunctionSlug;
  title: string;
  framing: string;
  index: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      <Link
        to={getFunctionPath(slug)}
        className={`glass-card p-8 hover-lift group border-t-2 ${accentColors[index]} flex flex-col h-full`}
      >
        <div className="mb-4">
          <FunctionIllustration
            slug={slug}
            className="w-[100px] h-auto object-contain mb-4 group-hover:scale-105 transition-transform duration-300"
          />
          <h2 className="text-xl font-bold group-hover:text-primary transition-colors">
            {title}
          </h2>
        </div>
        <p className="text-muted-foreground text-sm md:text-base leading-relaxed flex-1">
          {framing}
        </p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
          See examples
          <ArrowRight
            className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform"
            aria-hidden="true"
          />
        </span>
      </Link>
    </motion.div>
  );
};

const PullQuote: React.FC<{ quote: string; name: string; company: string }> = ({
  quote,
  name,
  company,
}) => (
  <motion.blockquote
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={defaultViewport}
    transition={{ duration: 0.5 }}
    className="text-center max-w-2xl mx-auto py-8"
  >
    <p className="text-lg md:text-xl italic text-foreground/80 leading-relaxed">
      "{quote}"
    </p>
    <cite className="block mt-3 text-sm font-medium text-muted-foreground not-italic">
      — {name}, {company}
    </cite>
  </motion.blockquote>
);

export const UseCasesSection = () => {
  return (
    <section className="container-class relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-mesh opacity-10 pointer-events-none" />
      <div className="section-inner relative z-10 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {MEMBER_SHOWCASE_FUNCTIONS.map((fn, index) => (
            <FunctionTile
              key={fn.slug}
              slug={fn.slug}
              title={fn.title}
              framing={fn.framing}
              index={index}
            />
          ))}
        </div>

        <PullQuote {...pullQuotes[0]} />
        <PullQuote {...pullQuotes[1]} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={defaultViewport}
          transition={{ duration: 0.5 }}
          className="text-center pt-4"
        >
          <p className="text-lg md:text-xl text-foreground/80 mb-4">
            These are the kinds of problems you'll learn to solve.
          </p>
          <Link
            to="/curriculum"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline underline-offset-4 text-lg transition-colors"
          >
            See the curriculum →
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
